import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from './theme';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import PublicLayout from './components/PublicLayout';
import { Suspense, lazy } from 'react';
import LoadingSpinner from './components/LoadingSpinner';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Lazy load components
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const GymManagement = lazy(() => import('./pages/GymManagement/index'));
const Flows = lazy(() => import('./pages/Flows'));
const MessagesLogs = lazy(() => import('./pages/MessagesLogs'));
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
const Features = lazy(() => import('./pages/Features'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
// const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ChatBotPage = lazy(() => import('./pages/ChatBot'));
const PostSignup = lazy(() => import('./pages/PostSignup'));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ToastProvider>
          <Router>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/about" element={<AboutUs />} />
                  {/* <Route path="/pricing" element={<Pricing />} /> */}
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                </Route>
                <Route
                  path="/post-signup"
                  element={
                    <ProtectedRoute>
                      <PostSignup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="messages" element={<MessagesLogs />} />
                  <Route path="gyms" element={<GymManagement />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="flows" element={<Flows />} />
                  <Route path="chat-bot" element={<ChatBotPage />} />
                  <Route path="api-docs" element={<ApiDocs />} />
                  <Route path="profile" element={<Profile />} />
                  <Route index element={<Navigate to="/dashboard/messages" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Router>
        </ToastProvider>
      </AuthProvider>
      {/* Add this temporary debug section */}
      {process.env.REACT_APP_SHOW_ENV_DATA === 'true' && (
        <div style={{ position: 'fixed', bottom: 0, right: 0, background: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px', fontSize: '12px' }}>
          <h4>Environment Variables:</h4>
          <pre>
            {JSON.stringify({
              REACT_APP_API_URL: process.env.REACT_APP_API_URL,
              REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
              REACT_APP_FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL,
              REACT_APP_API_VERSION: process.env.REACT_APP_API_VERSION,
            }, null, 2)}
          </pre>
        </div>
      )}
    </ThemeProvider>
  );
}

export default App;
